import i18next from 'i18next';
import type { UserAPI } from '../api';
import { getAPI } from './get-api';

type I18NextTOptions = Parameters<typeof i18next.t>[2];
export interface TranslationOptions {
  args?: string[];
  language?: string;
}

/**
 * Creates a translator function for a specific translation bundle.
 * @param bundleIdentifier - Identifier of the translation bundle.
 * @returns A translator function that takes a key, defaultValue, and optional options,
 * and returns the corresponding translation from the bundle.
 */
export function createTranslator(
  bundleIdentifier: string,
): (key: string, defaultValue: string, options?: TranslationOptions & I18NextTOptions) => string {
  const resources = window?.SYDLE?.translationResources;
  if (!i18next.isInitialized) {
    i18next.init({
      resources,
      interpolation: {
        prefix: '{',
        suffix: '}',
        escapeValue: false,
      },
    });
  }

  return (key: string, defaultValue: string, options?: TranslationOptions & I18NextTOptions): string => {
    const userAPI = getAPI<UserAPI>('userAPI');
    const currentLanguage = options?.language || userAPI?.getCurrentLanguage();
    if (!currentLanguage) {
      return defaultValue;
    }

    i18next.changeLanguage(currentLanguage);

    const i18nextOptions = {
      defaultValue: defaultValue,
      lng: currentLanguage,
      ...options,
    };

    const args = options?.args;
    if (args && args.length > 0) {
      args.forEach((arg, index) => {
        i18nextOptions[index] = arg;
      });
    }

    return i18next.t(bundleIdentifier + ':' + key, i18nextOptions);
  };
}
